import {
  applyReportParametersToURLSearchParams,
  DashboardRefreshPayload,
  DependencyError,
  ExpressionReturnType,
  mergeParameterValues,
  parseFormData,
  ReportParameterValues
} from '@cumu/shared';
import { controller, target, targets } from '@github/catalyst';
import type { FormBehaviorElement } from '../forms/behavior-element';
import { busySubmitButtons } from '../forms/utilities';
import { displayServerValidationError } from '../forms/validation';

@controller('dashboard-controller')
class DashboardControllerElement extends HTMLElement {
  @target declare keyScript: HTMLScriptElement;
  @target declare parametersForm: HTMLFormElement;
  @targets declare elements: HTMLElement[];
  declare key: string;
  declare parameter_types: Record<string, ExpressionReturnType>;
  private busy = false;

  connectedCallback() {
    this.key = JSON.parse(this.keyScript.textContent!);
    this.parameter_types = JSON.parse(this.getAttribute('parameter_types')!);

    const date_loaded = document
      .getElementById('dashboard-refresh-time')
      ?.getAttribute('datetime');
    if (
      date_loaded &&
      Date.now() - new Date(date_loaded).getTime() > 1000 * 60 * 6 // 6 minutes
    ) {
      this.refresh(true);
    }
  }

  async submitParameters(event: CustomEvent<FormData>) {
    event.preventDefault();
    const behavior = event.target as FormBehaviorElement;
    await Promise.resolve();
    await this.refresh(false);
    behavior.acceptChanges();
  }

  propagateParameters(parameter_values: ReportParameterValues) {
    for (const element of this.elements) {
      const element_type = element.getAttribute('element-type');
      if (element_type === 'report') {
        const parameter_names_json = element.getAttribute(
          'inherited-parameter-names'
        );
        if (!parameter_names_json) {
          continue;
        }
        const grid = element.querySelector('report-grid');
        if (!grid) {
          continue;
        }
        const parameter_names = JSON.parse(parameter_names_json) as string[];
        for (const name of parameter_names) {
          const value = parameter_values[name];
          if (value === undefined || value === null) {
            continue;
          }
          const input = grid.parametersForm.querySelector<HTMLInputElement>(
            `input[name^="parameter_values/${name}:"]`
          );
          if (!(input instanceof HTMLInputElement)) {
            throw new Error(
              `Expected report-grid parameter input for inherited parameter "${name}".`
            );
          }
          input.value = Array.isArray(value)
            ? JSON.stringify(value)
            : value.toString();
        }
      } else if (element_type === 'report-links') {
        for (const anchor of element.querySelectorAll<HTMLAnchorElement>(
          'a[inherited-parameter-names]'
        )) {
          const parameter_names_json = anchor.getAttribute(
            'inherited-parameter-names'
          )!;
          const parameter_names = JSON.parse(parameter_names_json) as string[];
          const inherited_parameter_values = Object.fromEntries(
            Object.entries(parameter_values).filter(([key]) =>
              parameter_names.includes(key)
            )
          );
          anchor.search = applyReportParametersToURLSearchParams(
            new URL(anchor.href, location.href).searchParams,
            inherited_parameter_values
          ).toString();
        }
      }
    }
  }

  async refresh(cardsOnly: boolean) {
    if (this.busy) {
      return;
    }
    this.busy = true;
    const url = this.getAttribute('refresh-source')!;
    const { parameter_values: selected_parameter_values = {} } = parseFormData<{
      parameter_values?: ReportParameterValues;
    }>(new FormData(this.parametersForm));
    let response: Response;
    try {
      busySubmitButtons(this.parametersForm, true);
      for (const element of this.elements) {
        element.classList.add('shimmer');
      }
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          key: this.key,
          parameter_values: selected_parameter_values
        })
      });
    } finally {
      busySubmitButtons(this.parametersForm, false);
      for (const element of this.elements) {
        element.classList.remove('shimmer');
      }
      this.busy = false;
    }
    if (!response.ok) {
      displayServerValidationError(this.parametersForm);
      throw new DependencyError(response, await response.text());
    }

    const locationUrl = new URL(location.href);
    applyReportParametersToURLSearchParams(
      locationUrl.searchParams,
      selected_parameter_values
    );
    history.replaceState(null, '', locationUrl.href);

    const {
      elements,
      parameter_values: calculated_parameter_values,
      date_loaded
    }: DashboardRefreshPayload = await response.json();

    document
      .getElementById('dashboard-refresh-time')
      ?.setAttribute('datetime', date_loaded + 'Z');

    const parameter_values = mergeParameterValues(
      selected_parameter_values,
      calculated_parameter_values
    );
    this.propagateParameters(parameter_values);

    for (let index = 0; index < this.elements.length; index++) {
      const element = this.elements[index];
      const element_type = element.getAttribute('element-type');
      const result = elements[index];
      if (!result || result.type !== element_type) {
        // Element mismatch, reload the page. Can happen while editing the dashboard.
        location.reload();
      }
      element.hidden = !result.visible;
      if (result.template) {
        element.insertAdjacentHTML('afterend', result.template);
        element.remove();
      } else {
        switch (result.type) {
          case 'report': {
            if (result.visible && !cardsOnly) {
              const grid = element.querySelector('report-grid');
              grid?.submitParameters();
            }
          }
        }
      }
    }
  }
}

declare global {
  interface Window {
    DashboardControllerElement: typeof DashboardControllerElement;
  }
  interface HTMLElementTagNameMap {
    'dashboard-controller': DashboardControllerElement;
  }
}
